import { useQuery } from '@tanstack/react-query';
import { inboxService } from '@/shared/services/inbox.service';
import { useMyRole } from '@/shared/hooks/useMyRole';
import { canReadInbox } from '@/shared/lib/role-access';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useAgentReadAllowed } from '@/shared/hooks/useInboxAgent';
import { INBOX_POLL_MS } from '@/shared/lib/inbox-poll';

/**
 * The two numbers on the Inbox nav item: unread messages, and conversations
 * escalated to the signed-in person (assigned to them, or unclaimed on a
 * level they sit on). One small request for the counts (GET
 * /inbox/conversations/counts), never every conversation state (IA-07).
 * It shares the `inbox` query key prefix, so a change in the inbox moves it.
 */
export function useInboxBadges(): { unread: number; escalated: number } {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  // A viewer, client or billing admin has no inbox (AL-06): nothing to ask for.
  const { role } = useMyRole();
  const mayRead = canReadInbox(role) && role !== null;
  // Escalations exist only on Agency; below it there is nothing to count.
  const agency = useAgentReadAllowed('agency');
  const counts = useQuery({
    queryKey: ['inbox', 'conversation-counts', wsId],
    queryFn: () => inboxService.conversationCounts(),
    enabled: !!wsId && mayRead,
    refetchInterval: INBOX_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  return {
    unread: mayRead ? counts.data?.unreadCount ?? 0 : 0,
    escalated: agency && mayRead ? counts.data?.counts.escalated_to_me ?? 0 : 0,
  };
}
